import { isEditorLive } from '@heidi/editor/core';
import { getDialogContainer, Surface } from '@heidi/editor/ui';
import { useIsMobile, useLatestRef } from '@heidi/utils';
import { TextSelection, type EditorState } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { getEditorScrollTarget } from './text-menu-scroll-target';

const DETACHED_RECT = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  toJSON() {
    return this;
  },
} as DOMRect;

/**
 * Zero-width anchor at the selection's horizontal center, on its first line.
 * Multi-line selections center on the content column, because the endpoint
 * coordinates skew toward wherever the last line ends.
 */
const getSelectionCenterAnchor = (
  editor: Editor
): { contextElement?: Element; getBoundingClientRect: () => DOMRect } | null => {
  // Returning null would make TipTap read the destroyed view, so anchor nowhere.
  if (!isEditorLive(editor, 'selection-ask-heidi-bubble.anchor')) {
    return { getBoundingClientRect: () => DETACHED_RECT };
  }
  const { selection } = editor.state;
  if (!(selection instanceof TextSelection) || selection.empty) {
    return null;
  }
  const { view } = editor;
  const start = view.coordsAtPos(selection.from);
  const end = view.coordsAtPos(selection.to, -1);
  const lineHeight = start.bottom - start.top;
  // Tops within half a line of each other read as the same visual line.
  const isMultiLine = end.top - start.top > lineHeight / 2;
  const centerX = isMultiLine
    ? (() => {
        const content = view.dom.getBoundingClientRect();
        return (content.left + content.right) / 2;
      })()
    : (start.left + end.left) / 2;
  const rect = {
    top: start.top,
    bottom: start.bottom,
    left: centerX,
    right: centerX,
    width: 0,
    height: lineHeight,
    x: centerX,
    y: start.top,
    toJSON() {
      return this;
    },
  } as DOMRect;
  // Lets floating-ui autoUpdate find the note scroller's ancestors.
  return { contextElement: view.dom, getBoundingClientRect: () => rect };
};

/**
 * Body append breaks BubbleMenu's blur hide, so close when focus leaves both
 * the editor and the bubble. BubbleMenu itself stays mounted: unmounting it
 * unregisters its plugin, which destroys every plugin view (e.g. an open slash menu).
 */
const useFocusLeaveGate = (
  editor: Editor,
  menuHostRef: RefObject<HTMLDivElement | null>
) => {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (!isEditorLive(editor, 'selection-ask-heidi-bubble.focusGate')) return;
    let pendingFrame: number | null = null;

    const closeOnFocusLeave = () => {
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        // The editor can be destroyed between the focus change and this frame.
        if (!isEditorLive(editor, 'selection-ask-heidi-bubble.focusGateFrame')) return;
        const active = document.activeElement;
        if (
          active &&
          (editor.view.dom.contains(active) || menuHostRef.current?.contains(active))
        ) {
          return;
        }
        setIsOpen(false);
      });
    };
    const reopenOnEditorFocus = () => setIsOpen(true);

    editor.on('blur', closeOnFocusLeave);
    editor.on('focus', reopenOnEditorFocus);
    document.addEventListener('focusin', closeOnFocusLeave);
    return () => {
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
      editor.off('blur', closeOnFocusLeave);
      editor.off('focus', reopenOnEditorFocus);
      document.removeEventListener('focusin', closeOnFocusLeave);
    };
  }, [editor, menuHostRef]);

  return isOpen;
};

/**
 * Bubble frame for the selection toolbar, centered above the highlight. It
 * stays visible while focus is inside it, so opening the Ask Heidi dropdown
 * does not dismiss it.
 */
export const SelectionAskHeidiBubble = ({
  editor,
  shouldShow,
  onShow,
  onHide,
  children,
}: {
  editor: Editor;
  shouldShow: (props: { view: EditorView; state: EditorState; from: number }) => boolean;
  onShow?: () => void;
  onHide?: () => void;
  children: ReactNode;
}) => {
  const isMobile = useIsMobile();
  // BubbleMenu snapshots `shouldShow` once per editor, so the predicate reads these.
  const latestPredicateInputsRef = useLatestRef({ isMobile, shouldShow, onShow, onHide });
  const [scrollTarget, setScrollTarget] = useState<HTMLElement | Window | null>(null);

  // BubbleMenu also snapshots `options.scrollTarget`, so register only after the
  // note scroller resolves; otherwise it tracks window and drifts in the note.
  useEffect(() => {
    if (!isEditorLive(editor, 'selection-ask-heidi-bubble.scrollTarget')) return;
    const resolveScrollTarget = () => {
      // Also runs from a rAF that can land after the editor is destroyed.
      if (!isEditorLive(editor, 'selection-ask-heidi-bubble.resolveScrollTarget')) return;
      if (!editor.view.dom.isConnected) return;
      setScrollTarget(getEditorScrollTarget(editor));
    };
    resolveScrollTarget();
    editor.on('create', resolveScrollTarget);
    const frame = requestAnimationFrame(resolveScrollTarget);
    return () => {
      editor.off('create', resolveScrollTarget);
      cancelAnimationFrame(frame);
    };
  }, [editor]);

  const menuHostRef = useRef<HTMLDivElement | null>(null);
  const isFocusGateOpen = useFocusLeaveGate(editor, menuHostRef);

  // The plugin makes its host a Tab stop and never hides it on an outside blur,
  // so the empty host left by a closed gate must not stay focusable.
  useEffect(() => {
    if (menuHostRef.current) menuHostRef.current.tabIndex = isFocusGateOpen ? 0 : -1;
  }, [isFocusGateOpen, scrollTarget]);

  // A closed gate hides the contents without the predicate reporting false.
  useEffect(() => {
    if (!isFocusGateOpen) latestPredicateInputsRef.current.onHide?.();
  }, [isFocusGateOpen, latestPredicateInputsRef]);

  if (!scrollTarget) return null;

  return (
    <BubbleMenu
      // Distinct from TableMenu: TipTap unregisterPlugin prefix-matches the key.
      pluginKey="textMenu"
      ref={menuHostRef}
      editor={editor}
      className="relative z-50"
      appendTo={() => getDialogContainer(editor, { fallbackToBody: true })}
      getReferencedVirtualElement={() => getSelectionCenterAnchor(editor)}
      shouldShow={({ view, state, from, element }) => {
        const latest = latestPredicateInputsRef.current;
        const isChildOfMenu = element.contains(document.activeElement);
        const isVisible =
          !latest.isMobile &&
          (view.hasFocus() || isChildOfMenu) &&
          latest.shouldShow({ view, state, from });
        if (isVisible) {
          latest.onShow?.();
        } else {
          latest.onHide?.();
        }
        return isVisible;
      }}
      options={{
        scrollTarget,
        placement: 'top',
        offset: 8,
        flip: { fallbackPlacements: ['bottom'], padding: 8 },
        shift: { padding: 8 },
      }}
    >
      {isFocusGateOpen && isEditorLive(editor, 'selection-ask-heidi-bubble.surface') ? (
        <Surface
          data-selection-bubble-menu
          className="flex flex-row items-center gap-0.5 px-2 py-1"
          onMouseDown={(e) => {
            // Keep the editor selection alive while interacting with the row.
            e.preventDefault();
          }}
        >
          {children}
        </Surface>
      ) : null}
    </BubbleMenu>
  );
};
